import type { GuideGroup } from './guideContent';

// Case graph guide. Copy is verbatim from the Figma file "Network graph"
// (rIKeruJP5BypnUY6xyazSz), flow 1150-2 "Case graph guide" plus the Filtering
// page (frames 1292-505 / 1293-855, 2026-10); `id`s are the "Learn more" /
// search anchors, see guideContent.ts.

export const CASE_GUIDE_GROUPS: GuideGroup[] = [
  {
    label: '',
    pages: [
      {
        id: 'welcome',
        title: 'Getting started with Case graph',
        navTitle: 'Getting started',
        blocks: [
          {
            type: 'p',
            text: 'The Case graph shows how actors in a Case are connected through reposts. It helps you see which actors form communities, who produces or amplifies content, and how a narrative spreads over time.',
          },
          {
            type: 'p',
            text: 'The graph is built from reposts found in the selected Case and time period.',
          },
          { type: 'h', text: 'What you can find out' },
          {
            type: 'tiles',
            items: [
              {
                page: 'uc-communities',
                title: 'How actors group into communities',
                text: 'See which actors repost each other and form communities, and which communities remain separate.',
              },
              {
                page: 'uc-spread',
                title: 'How a narrative spreads over time',
                text: 'See when actors first appear in the Case and how repost activity develops over time.',
              },
              {
                page: 'uc-roles',
                title: 'Who creates content and who spreads it',
                text: 'See which actors mainly produce content and which mainly amplify it through reposts.',
              },
              {
                page: 'uc-mixed',
                title: 'Which actors both create and repost',
                text: 'See which actors both get reposted and repost others by comparing their inbound and outbound connections.',
              },
              {
                page: 'uc-hra',
                title: 'Where high-risk actors appear',
                text: 'See which communities contain High Risk Actors and when those actors first appear in the Case.',
              },
              {
                page: 'uc-strong-ties',
                title: 'Which actors repost each other most',
                text: 'See where repost activity is strongest, including actors that repost each other in both directions.',
              },
            ],
          },
          { type: 'h', text: 'How the graph works' },
          {
            type: 'cards',
            items: [
              {
                page: 'configuration',
                title: 'Configuration',
                text: 'Graph layout, node size, color, and display options.',
              },
              {
                page: 'filtering',
                title: 'Filtering',
                text: 'Filter actors and connections by platform, role, community, and more.',
              },
              {
                page: 'how-to-read',
                title: 'How to read the graph',
                text: 'Nodes, connections, communities, categories, and other graph elements.',
              },
              {
                page: 'calculations',
                title: 'Calculations',
                text: 'How communities, categories, and other graph values are calculated.',
              },
              {
                page: 'limitations',
                title: 'Understanding the results',
                text: 'Context, limitations, and other details that help explain what the graph shows.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'What you can find out',
    pages: [
      {
        id: 'uc-communities',
        title: 'How actors group into communities',
        navTitle: 'Communities of actors',
        blocks: [
          {
            type: 'p',
            text: 'See which actors form communities based on how they repost each other.',
          },
          {
            type: 'p',
            text: 'Communities are calculated using the Louvain method. It groups actors based on how often they repost each other.',
          },
          {
            type: 'p',
            text: 'Some communities may be closely connected, while others may have little or no connection to each other. This helps you spot groups of actors that frequently repost each other and may be worth looking at more closely.',
          },
          { type: 'h', text: 'Set up the graph' },
          {
            type: 'steps',
            items: [
              {
                text: 'Set **Node size metric** to **Degree**.',
                why: 'Actors with more connections appear larger, which helps you spot the most connected actors in each community.',
              },
              {
                text: 'Set **Layout** to **Separated clusters**.',
                why: 'This places each community in its own space, making the groups easier to distinguish.',
              },
              {
                text: 'Set **Color scheme** to **By community**.',
                why: 'Actors in the same community share the same color.',
              },
              {
                text: 'Set **Cross-cluster links** to **Dimmed**.',
                why: 'Connections between communities stay visible but appear faded, so the communities are easier to see separately.',
              },
              {
                text: 'Optional: turn on **Thickness by interactions**.',
                why: 'Connections with more reposts appear thicker, which helps you see where more repost activity happens.',
              },
            ],
          },
          { type: 'h', text: 'Communities are not topics', id: 'not-topics' },
          {
            type: 'p',
            text: 'Communities are based on who reposts whom, not on what actors post about. Two separate communities can spread the same narrative without reposting each other. Actors in the same community can also post about different topics.',
          },
          {
            type: 'related',
            items: [
              { page: 'configuration', section: 'layout', title: 'Layout' },
              {
                page: 'configuration',
                section: 'color-scheme',
                title: 'Color scheme',
              },
              {
                page: 'configuration',
                section: 'cross-cluster',
                title: 'Cross-cluster links',
              },
              {
                page: 'configuration',
                section: 'size-metric',
                title: 'Node size metric',
              },
              {
                page: 'configuration',
                section: 'display-options',
                title: 'Display options',
              },
            ],
          },
        ],
      },
      {
        id: 'uc-spread',
        title: 'How a narrative spreads over time',
        navTitle: 'Narrative spread',
        blocks: [
          {
            type: 'p',
            text: 'See how repost activity develops during the Case and when actors and connections first appear.',
          },
          {
            type: 'p',
            text: 'Use the Activity timeline to watch actors and connections appear as you move through the selected period.',
          },
          { type: 'h', text: 'Set up the graph' },
          {
            type: 'steps',
            items: [
              'Turn on **Activity timeline** in **Configuration**.',
              {
                text: 'Turn on **Direction arrows**.',
                why: 'This makes it easier to see who reposted whom and distinguish inbound and outbound connections.',
              },
              {
                text: 'Optional: turn on **Thickness by interactions**.',
                why: 'Connections with more reposts appear thicker.',
              },
              {
                text: 'Optional: set **Color scheme** to **By platform**.',
                why: 'Use this if you want to see when actors from different platforms appear.',
              },
            ],
          },
          { type: 'h', text: 'Use the timeline' },
          {
            type: 'steps',
            items: [
              'Select the time range you want to examine.',
              'Press **Play** to watch actors and connections appear day by day.',
              'Change the playback speed to move through the Case faster or slower.',
              'Pause at any point to inspect the actors and connections shown at that moment.',
            ],
          },
          {
            type: 'related',
            items: [
              {
                page: 'configuration',
                section: 'display-options',
                title: 'Display options',
              },
              {
                page: 'configuration',
                section: 'color-scheme',
                title: 'Color scheme',
              },
            ],
          },
        ],
      },
      {
        id: 'uc-roles',
        title: 'Who creates content and who spreads it',
        navTitle: 'Producers and amplifiers',
        blocks: [
          {
            type: 'p',
            text: 'See which actors mainly produce content and which mainly spread it through reposts. You can look at these roles across the whole Case or within a specific community. This helps you see where content starts and which actors help it reach more people.',
          },
          { type: 'h', text: 'Set up the graph' },
          {
            type: 'steps',
            items: [
              'Open the **Actors panel**.',
              {
                text: 'Under **Categories**, select **Producer** or **Amplifier**.',
                why: 'The actors in that category are highlighted on the graph, and you can open the list to see them individually.',
              },
              'Switch between **Producer** and **Amplifier** to compare who mainly gets reposted with who mainly reposts others.',
              {
                text: 'To analyze a community instead, select it under **Communities**.',
                why: 'The community is highlighted on the graph, and its actors are listed together.',
              },
              {
                text: 'Optional: turn on **Direction arrows** in **Configuration**.',
                why: 'This makes it easier to see who reposted whom when you inspect individual actors.',
              },
              'Select an actor to open **Actor details** and review its role and connections.',
            ],
          },
          {
            type: 'related',
            items: [
              {
                page: 'how-to-read',
                section: 'categories',
                title: 'Actor categories',
              },
              {
                page: 'how-to-read',
                section: 'communities',
                title: 'Communities',
              },
              {
                page: 'configuration',
                section: 'display-options',
                title: 'Display options',
              },
            ],
          },
        ],
      },
      {
        id: 'uc-mixed',
        title: 'Which actors both create and repost',
        navTitle: 'Actors in both roles',
        blocks: [
          {
            type: 'p',
            text: 'See which actors both get reposted and repost content from others. These actors have a **Mixed** role. Looking at both inbound and outbound connections helps you see how they take part in both creating and spreading content.',
          },
          { type: 'h', text: 'Set up the graph' },
          {
            type: 'steps',
            items: [
              'Open the **Actors panel**.',
              {
                text: 'Under **Categories**, select **Mixed**.',
                why: 'The actors in this category are highlighted on the graph and listed together.',
              },
              {
                text: 'Optional: turn on **Direction arrows** in **Configuration**.',
                why: 'This helps you see which connections are inbound and which are outbound.',
              },
              'Select an actor to open **Actor details** and review its inbound, outbound, and mutual connections.',
            ],
          },
          {
            type: 'related',
            items: [
              {
                page: 'how-to-read',
                section: 'categories',
                title: 'Actor categories',
              },
              {
                page: 'configuration',
                section: 'display-options',
                title: 'Display options',
              },
              {
                page: 'how-to-read',
                section: 'nodes-connections',
                title: 'Connections',
              },
            ],
          },
        ],
      },
      {
        id: 'uc-hra',
        title: 'Where high-risk actors appear',
        navTitle: 'High-risk actors',
        blocks: [
          {
            type: 'p',
            text: 'See which High Risk actors are present in the Case and where they appear on the graph. Selecting the **High Risk Actors** category highlights those actors on the graph and shows them as a list. You can then see which communities they belong to and inspect individual actors more closely.',
          },
          { type: 'h', text: 'Set up the graph' },
          {
            type: 'steps',
            items: [
              'Open the **Actors panel**.',
              {
                text: 'Under **Categories**, select **High Risk Actors**.',
                why: 'High Risk actors are highlighted on the graph and listed in the panel.',
              },
              'Select an actor to open **Actor details** and review its community and connections.',
              'Turn on **Activity timeline** in **Configuration**.',
              'Use the timeline to see when High Risk actors appear during the Case.',
              'Press **Play** to watch them appear as repost activity develops over time.',
              'Change the playback speed or select a shorter date range if you want to examine a specific period more closely.',
            ],
          },
          {
            type: 'related',
            items: [
              {
                page: 'how-to-read',
                section: 'timeline',
                title: 'Activity timeline',
              },
            ],
          },
        ],
      },
      {
        id: 'uc-strong-ties',
        title: 'Which actors repost each other most',
        navTitle: 'Strongest interactions',
        blocks: [
          {
            type: 'p',
            text: 'See which actors have the most repost activity and which actors are connected to the most others. Larger nodes help you spot actors with more connections. Thicker connections show where more reposts happened between the same actors. Mutual connections show pairs that repost each other in both directions. Frequent mutual reposting can be worth looking at more closely, but it does not by itself prove coordination.',
          },
          { type: 'h', text: 'Set up the graph' },
          {
            type: 'steps',
            items: [
              {
                text: 'Set **Node size metric** to **Degree**.',
                why: 'Actors with more connections appear larger.',
              },
              'Optional: set **Node size metric** to **PageRank** instead if you want node size to also reflect who those connections come from.',
              {
                text: 'Turn on **Thickness by interactions**.',
                why: 'Connections with more reposts appear thicker.',
              },
              {
                text: 'Turn on **Direction arrows**.',
                why: 'This shows who reposted whom.',
              },
              'Look for large nodes to find actors with many connections.',
              'Look for thick connections to find pairs with more repost activity.',
              'Look for **mutual connections** to find pairs that repost each other in both directions.',
              'Select an actor to open **Actor details** and review its inbound, outbound, and mutual connections.',
              'Treat frequent or mutual reposting as a signal to investigate further, not as proof of coordination.',
            ],
          },
          {
            type: 'related',
            items: [
              {
                page: 'configuration',
                section: 'size-metric',
                title: 'Node size metric',
              },
              {
                page: 'configuration',
                section: 'display-options',
                title: 'Display options',
              },
              {
                page: 'how-to-read',
                section: 'nodes-connections',
                title: 'Connections',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'How the graph works',
    pages: [
      {
        id: 'configuration',
        title: 'Configuration',
        blocks: [
          {
            type: 'p',
            text: 'Use Configuration to change how the graph looks. These settings do not change the data or calculations behind the graph.',
          },
          { type: 'h', text: 'Node size metric', id: 'size-metric' },
          {
            type: 'p',
            text: 'Each node represents an actor. This setting controls how large each node appears on the graph.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'degree',
                key: 'Degree',
                text: 'Shows node size based on the number of connections.',
                rule: 'More connections = a larger node.',
                why: 'For example, an actor with 20 connections appears larger than an actor with 5.',
              },
              {
                id: 'pagerank',
                key: 'PageRank',
                text: 'Shows node size based on both the number of connections and who those connections come from.',
                rule: 'More connections from actors that also have many connections = a larger node.',
                why: 'A connection from a highly connected actor increases the node size more than a connection from an actor with only a few connections. This means an actor with fewer connections can still appear larger if those connections come from highly connected actors.',
              },
            ],
          },
          { type: 'h', text: 'Layout', id: 'layout' },
          {
            type: 'p',
            text: 'Changes where actors and communities appear on the graph. The connections stay the same. Only their position on the screen changes.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'layout-organic',
                key: 'Organic bridges',
                text: 'Keeps connected communities closer together.',
                rule: 'More connections between two communities = they appear closer together.',
                why: 'Actors with connections to more than one community tend to appear between those communities.',
              },
              {
                id: 'layout-packed',
                key: 'Separated clusters',
                text: 'Places each community in its own separate group.',
                rule: 'Each community gets its own space on the graph.',
                why: 'This makes it easier to see the communities separately and compare their size.',
              },
            ],
          },
          {
            type: 'note',
            text: 'Distance on the screen does not show connection strength.',
          },
          { type: 'h', text: 'Color scheme', id: 'color-scheme' },
          {
            type: 'p',
            text: 'Changes what the colors on the graph represent. The legend in the bottom-left corner shows what each color means.',
          },
          {
            type: 'rows',
            rows: [
              {
                key: 'By community',
                text: 'Actors in the same community share the same color. Connections use the color of their community.',
              },
              {
                key: 'By interaction type',
                text: 'Connections use different colors for different interaction types. In a Case graph, all connections are reposts, so they use the same color.',
              },
              {
                key: 'By platform',
                text: 'Actors from the same platform share the same color. The legend also shows how many actors come from each platform.',
              },
              {
                key: 'Monochrome',
                text: 'Shows the whole graph in one color.',
              },
            ],
          },
          { type: 'h', text: 'Cross-cluster links', id: 'cross-cluster' },
          {
            type: 'p',
            text: 'Controls how connections between different communities are shown. Connections inside the same community stay unchanged.',
          },
          {
            type: 'rows',
            rows: [
              {
                key: 'Dimmed',
                text: 'Connections between different communities appear faded. You can still see them, but they are less prominent.',
              },
              {
                key: 'Hidden',
                text: 'Connections between different communities are hidden. This leaves only the connections inside each community visible.',
              },
              {
                key: 'Full',
                text: 'Connections between different communities are fully visible. They are shown the same way as connections inside a community.',
              },
            ],
          },
          { type: 'h', text: 'Display options', id: 'display-options' },
          {
            type: 'p',
            text: 'Use Display options to choose which visual elements are shown.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'direction-arrows',
                key: 'Direction arrows',
                text: 'Adds arrows to connections so you can see the direction of each repost. The arrow points from the actor who reposted to the actor whose content was reposted.',
              },
              {
                id: 'thickness',
                key: 'Thickness by interactions',
                text: 'Changes connection thickness based on how many reposts happened between two actors.',
                rule: 'More reposts = a thicker connection.',
                why: 'When this option is off, all connections stay thin. Connections of the selected actor still show their relative thickness.',
              },
              {
                id: 'highlight-bridges',
                key: 'Highlight critical bridges',
                text: 'Adds a ring around actors that connect multiple communities. Critical bridges are actors that link different communities together. See [[calculations#critical-bridges|Critical bridges]] for more details.',
              },
              {
                id: 'timeline',
                key: 'Activity timeline',
                text: 'Shows the Activity timeline panel below the graph. The timeline shows repost activity over time. See [[how-to-read#timeline|Activity timeline]] for more details.',
              },
            ],
          },
        ],
      },
      {
        id: 'filtering',
        title: 'Filtering',
        blocks: [
          {
            type: 'p',
            text: 'Use **Filters** to focus on the actors and connections you want to look at. Filters change only what is shown on the graph. They do not change the data or calculations behind the graph.',
          },
          { type: 'h', text: 'How filters work', id: 'how-filters-work' },
          {
            type: 'p',
            text: 'Filters are grouped into two tabs: **Actors** and **Connections**.',
          },
          {
            type: 'list',
            items: [
              'Within one filter, the graph shows actors that match *any* of the selected values. For example, selecting **Twitter** and **Telegram** shows actors from either platform.',
              'Across different filters, the graph shows only actors that match *all* of them. For example, selecting **Twitter** in **Platforms** and **Amplifier** in **Actor roles** shows only Amplifiers on Twitter.',
              'If nothing is selected in a filter, that filter does not limit the graph.',
            ],
          },
          { type: 'h', text: 'Dim instead of hide', id: 'dim-instead-of-hide' },
          {
            type: 'p',
            text: 'By default, actors that do not match the filters are hidden. Turn on **Dim instead of hide** to keep them visible but dimmed.',
          },
          {
            type: 'note',
            text: 'This helps you see where the matching actors sit in the whole graph.',
          },
          { type: 'h', text: 'Actor filters', id: 'actor-filters' },
          {
            type: 'p',
            text: 'Use the **Actors** tab to choose which actors appear on the graph.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'platforms',
                key: 'Platforms',
                text: 'Shows actors from the selected platforms.',
              },
              {
                id: 'actor-roles',
                key: 'Actor roles',
                text: 'Shows actors with the selected role: Producer, Amplifier, or Mixed. See [[how-to-read#categories|Actor categories]] for what each role means.',
              },
              {
                id: 'high-risk-accounts',
                key: 'High risk accounts',
                text: 'Shows actors marked as High Risk in the platform, by High Risk type.',
                why: 'For example, **Information manipulation**.',
              },
              {
                id: 'state-affiliated',
                key: 'State affiliated',
                text: 'Shows actors marked as State Affiliated in the platform, by affiliation type.',
                why: 'For example, **Official state channels**, **State-controlled outlets**, or **State-linked channels**.',
              },
              {
                id: 'affiliation-country',
                key: 'Affiliation country',
                text: 'Shows State Affiliated actors by the country they are affiliated with.',
              },
              {
                id: 'communities',
                key: 'Communities',
                text: 'Shows actors from the selected communities.',
              },
              {
                id: 'origin-country',
                key: 'Origin country',
                text: 'Shows actors by the country they are from.',
              },
              {
                id: 'audience',
                key: 'Audience',
                text: 'Shows actors by audience size.',
                why: 'Audience is grouped into ranges: **< 1K**, **1K – 10K**, **10K – 100K**, **100K – 1M**, and **≥ 1M**.',
              },
            ],
          },
          {
            type: 'note',
            text: 'Long lists show the most common values first. Select **Show more** to see the rest.',
          },
          { type: 'h', text: 'Connection filters', id: 'connection-filters' },
          {
            type: 'p',
            text: 'Use the **Connections** tab to choose which connections appear on the graph.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'mutual-only',
                key: 'Mutual connections only',
                text: 'Shows only actors that have mutual connections, where two actors repost each other.',
              },
              {
                id: 'min-connections',
                key: 'Minimum connections',
                text: 'Shows only actors with at least the selected number of connections. Inbound and outbound connections are counted together.',
                why: 'Type a number or choose one of the suggested values. The panel shows how many actors match.',
              },
            ],
          },
          { type: 'h', text: 'Active filters', id: 'active-filters' },
          {
            type: 'p',
            text: 'Active filters appear above the graph. Each filter appears as one chip. If several values are selected, the chip shows the first value and how many more are selected, for example **Platforms Twitter +2**.',
          },
          {
            type: 'list',
            items: [
              'Select × on a chip to remove that filter.',
              'Select **Clear all filters** to remove all filters at once.',
            ],
          },
          {
            type: 'h',
            text: 'Filters and calculations',
            id: 'filters-calculations',
          },
          {
            type: 'p',
            text: 'Communities, actor roles, Degree, and PageRank are calculated for the whole graph. Filtering does not recalculate them. An actor keeps the same community and role when other actors are filtered out.',
          },
        ],
      },
      {
        id: 'how-to-read',
        title: 'How to read the graph',
        blocks: [
          {
            type: 'p',
            text: 'Learn what the graph shows and where to find details about actors, communities, and activity over time.',
          },
          { type: 'h', text: 'Nodes and connections', id: 'nodes-connections' },
          {
            type: 'p',
            text: 'Each node represents an actor. A connection between two nodes means that one actor reposted the other. The connection goes from the actor who reposted to the actor whose content was reposted.',
          },
          {
            type: 'list',
            items: [
              '**Inbound** shows how many actors reposted the selected actor.',
              '**Outbound** shows how many actors the selected actor reposted.',
              '**Mutual** shows how many actors both reposted the selected actor and were reposted by them.',
            ],
          },
          {
            type: 'p',
            text: 'If both actors repost each other, they have a **mutual connection**. Mutual connections appear slightly brighter.',
          },
          {
            type: 'note',
            text: 'A Case can include actors from multiple platforms. Connections never cross platforms because reposts happen within the same platform.',
          },
          {
            type: 'p',
            text: 'Node size and color depend on the settings you choose in [[configuration|Configuration]].',
          },
          { type: 'h', text: 'Communities', id: 'communities' },
          {
            type: 'p',
            text: 'A community is a group of actors that repost each other more often than they repost actors outside the group. Communities help you see which groups of actors tend to spread content together. See [[calculations#communities|How communities are calculated]] for more details.',
          },
          { type: 'h', text: 'Actor categories', id: 'categories' },
          {
            type: 'p',
            text: 'These categories describe the role or status of an actor in the Case graph.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'producer',
                key: 'Producer',
                text: 'Gets reposted but does not repost others.',
              },
              {
                id: 'amplifier',
                key: 'Amplifier',
                text: 'Reposts others but is not reposted.',
              },
              {
                id: 'mixed',
                key: 'Mixed',
                text: 'Both reposts others and gets reposted.',
              },
              {
                id: 'high-risk',
                key: 'High Risk Actor',
                text: 'Marked as High Risk in the platform. The graph shows this existing status; it does not calculate it.',
              },
              {
                id: 'state-affiliated',
                key: 'State Affiliated',
                text: 'Marked as State Affiliated in the platform. This means the actor is affiliated with a specific country or government. The graph shows this existing status; it does not calculate it.',
              },
              {
                id: 'critical-bridge',
                key: 'Critical bridge',
                text: 'Connects multiple communities. See [[calculations#critical-bridges|Critical bridges]] for more details.',
              },
              {
                id: 'high-interaction',
                key: 'High-Interaction Actor',
                text: 'Actors with a significantly higher number of interactions than other actors in this graph.',
              },
            ],
          },
          {
            type: 'p',
            text: 'Actor roles are calculated for the whole Case and do not change as you move through the timeline.',
          },
          { type: 'h', text: 'Actors panel', id: 'actors-panel' },
          {
            type: 'p',
            text: 'Use the Actors panel to browse actors by category or community.',
          },
          {
            type: 'rows',
            rows: [
              {
                key: 'Categories',
                text: 'Groups actors by their category in the Case graph. Select a category to highlight its actors on the graph and open the list of actors in that category.',
              },
              {
                key: 'Communities',
                text: 'Groups actors by community. Select a community to highlight its actors on the graph and open the list of actors in that community.',
              },
              {
                key: 'Create an Actor Group',
                text: 'You can create an **Actor Group** from a category or community. The Actor Group is saved in the platform and can be used outside the graph.',
              },
            ],
          },
          { type: 'h', text: 'Actor details', id: 'actor-details' },
          {
            type: 'p',
            text: 'Open **Actor details** by selecting an actor on the graph or from the Actors panel. It shows the actor’s categories, community, and connections.',
          },
          { type: 'h', text: 'Activity timeline', id: 'timeline' },
          {
            type: 'p',
            text: 'Shows how repost activity changes over time. Each bar represents the number of reposts for one day.',
          },
          {
            type: 'p',
            text: 'Turn on **Activity timeline** in **Configuration** to show it below the graph. Select a date range to focus on a specific period. Press **Play** to watch connections appear over time and see how the graph develops day by day. You can change the playback speed to move through the timeline faster or slower.',
          },
        ],
      },
      {
        id: 'calculations',
        title: 'Calculations',
        blocks: [
          {
            type: 'p',
            text: 'This section explains how the main values in the Case graph are calculated.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'communities',
                key: 'Communities',
                text: 'Communities are found using the **Louvain algorithm**. The calculation looks for groups where actors have stronger repost connections with each other than with actors outside the group. Repost direction is ignored. The number of reposts between actors is used as the connection weight.',
              },
              {
                id: 'degree',
                key: 'Degree',
                text: 'Degree counts how many unique actors are connected to an actor. Inbound and outbound connections are counted together. If the same two actors have many reposts between them, they still count as one connection for Degree.',
              },
              {
                id: 'pagerank',
                key: 'PageRank',
                text: 'PageRank considers both the number of connections to an actor and the PageRank of the actors those connections come from. A connection from an actor with a higher PageRank has more effect than a connection from an actor with a lower PageRank. The number of reposts in a connection is also used as its weight. PageRank is relative to the actors in the current graph. Use it to compare actors with each other, not against a fixed scale.',
              },
              {
                id: 'connections',
                key: 'Connections',
                text: 'Connections are counted by unique actors, not by the total number of reposts.',
                why: [
                  '**Inbound** counts how many actors reposted this actor.',
                  '**Outbound** counts how many actors this actor reposted.',
                  '**Mutual** counts how many actors have connections with this actor in both directions.',
                  'Multiple reposts between the same two actors do not create additional connections.',
                ],
              },
              {
                id: 'reposts',
                key: 'Reposts',
                text: 'Each repost adds to the repost count of the connection between the two actors.',
              },
              {
                id: 'critical-bridges',
                key: 'Critical bridges',
                text: 'Critical bridges are calculated from direct connections between actors. Connection direction is ignored. For each actor, the calculation counts how many other communities their direct connections reach. Multiple connections to the same community count once. An actor must connect to at least two other communities to qualify. Qualifying actors are ranked by how many communities they connect to. If actors have the same count, betweenness is used first, then the number of connections. Up to 10 actors are marked as critical bridges.',
              },
              {
                id: 'actor-roles',
                key: 'Actor roles',
                text: 'Actor roles are calculated from repost direction across the whole Case. Producer, Amplifier, and Mixed follow the rules described in [[how-to-read#categories|Actor categories]]. The role does not change when you move through the Activity timeline.',
              },
              {
                id: 'high-risk',
                key: 'High Risk / State Affiliated',
                text: 'This is the share of actors in a community that are marked **High Risk** or **State Affiliated** in the platform. The graph uses these existing statuses; it does not calculate them.',
              },
            ],
          },
        ],
      },
      {
        id: 'limitations',
        title: 'Understanding the results',
        blocks: [
          {
            type: 'p',
            text: 'The graph shows patterns in repost activity, but it does not show the full picture. Keep these limitations in mind when interpreting what you see.',
          },
          {
            type: 'rows',
            rows: [
              {
                id: 'not-every-actor',
                key: 'Not every actor is shown',
                text: 'Actors with fewer than two connections are removed from the graph. This check repeats until every actor left has at least two connections. As a result, smaller or less connected actors may not appear even if they are relevant to the Case. The graph can show up to **2,000 actors**. If more actors qualify, only the most active are kept.',
              },
              {
                id: 'period',
                key: 'The selected period changes the graph',
                text: 'The graph only includes repost activity from the selected time period. Changing the period can change which actors, connections, and communities appear.',
              },
              {
                id: 'more-activity',
                key: 'More activity does not mean coordination',
                text: 'A large number of reposts or a sudden increase in activity does not by itself mean that actors are coordinating. The same pattern can also appear around a major event or widely shared content.',
              },
              {
                id: 'less-activity',
                key: 'Less activity does not mean there is no coordination',
                text: 'Coordinated activity can be spread over time or across many actors. A graph with little visible activity does not prove that coordination is absent.',
              },
              {
                id: 'not-topics',
                key: 'Communities are not topics',
                text: 'Communities are based on who reposts whom, not on what actors post about. Two separate communities can spread the same narrative without reposting each other. Actors in the same community can also post about different topics.',
              },
              {
                id: 'not-complete',
                key: 'The graph is not a complete picture of an actor',
                text: 'The graph only shows the repost connections included in this Case and time period. An actor may have other activity or connections that are not represented here.',
                why: 'To explore an actor’s wider network, open the actor and generate a Single Actor Graph, or create an Actor Group Graph for a broader set of actors.',
              },
            ],
          },
        ],
      },
    ],
  },
];
